// @ts-check
/* eslint no-param-reassign: ["error", { "props": false }] */

/**
* A field, displayable on the page
* @typedef {Object} Field
* @property {string} group - Indicates whether the Courage component is present.
*/

import Vue from 'vue'
import Vuex from 'vuex'
import i18n from '../locales/'
import axios from 'axios'
import * as Sentry from '@sentry/browser'
import amplitude from 'amplitude-js'
import setWith from 'lodash.setwith'
import router from '../router'

import auth from './modules/auth'

Vue.use(Vuex)

const store = new Vuex.Store({
  strict: true,
  modules: {
    auth
  },
  state: {
    logoURL: '',
    hasBranding: false,
    config: {},
    clientInfo: {},
    envelope: {
      documents: [],
      title: null,
      id: null
    },
    parties: [],
    colours: {},
    currentDocument: 0, // index
    activeTabId: 0, // index
    activeFieldId: null,
    sequenceGuideNumber: 1, // number
    signatureToUse: null,
    latestSignature: null,
    signatures: {
      drawn: null,
      typed: null,
      uploaded: null,
      signatureData: null,
      encodedJPG: null
    },
    isMobile: false,
    uploaders: {},
    passwordRequired: false,
    requestedEnvelope: {
      envelope_fingerprint: null,
      signature_fingerprint: null,
      preview_encrypt: null,
      isPreview: null,
      is_new_encrypt: null,
      widgetCode: null
    },
    widget: {}
  },
  mutations: {
    setWidget: (state, { widget, mergeFields }) => {
      state.widget = widget

      if (mergeFields) {
        state.widget.mergeFields = Object.keys(mergeFields).map((key) => { // eslint-disable-line arrow-body-style
          return {
            fieldName: key,
            value: mergeFields[key]
          }
        })
      }

      Object.values(state.widget.fields).forEach((page) => {
        page.forEach((field) => {
          field.content = ''
          if (field.merge_field) {
            const mf = state.widget.mergeFields.find(mergeField => mergeField.fieldName === field.merge_field)
            if (mf && mf.value) {
              field.content = mf.value
              field.prefilled = true
            }
            field.is_merge_field = true
          }
        })
      })

      state.config = widget.config // Set company config
      if (widget.config.company_signature_default === 'upload') {
        state.signatureToUse = 'uploaded'
      } else {
        state.signatureToUse = widget.config.company_signature_default
      }

      state.envelope.title = widget.title // Set envelope details
      state.envelope.id = widget.id
      state.envelope.pages = widget.pages

      state.colours = widget.colours // Set colours

      state.logoURL = widget.logo // Set branding
      state.hasBranding = widget.hasBranding // Set branding
    },
    setEnvelopeRegular: (state, { data }) => {
      state.config = data.config // Set company config
      if (data.config.company_signature_default === 'upload') {
        state.signatureToUse = 'uploaded'
      } else {
        state.signatureToUse = data.config.company_signature_default
      }

      state.envelope.title = data.envelope_title // Set envelope details
      state.envelope.id = data.envelope_id

      state.colours = data.colours // Set colours

      state.logoURL = data.logoURL // Set branding
      state.hasBranding = data.hasBranding // Set branding

      data.parties.forEach((party) => { // Set parties
        state.parties.push(party)
      })

      /**
       * Logging info for potential issues - https://gitlab.wearedomo.com/domo/signable/signing-page/-/issues/166
       *
       * Wrapping it in a function so we can set sentry scope *only for capturing the message*, so as not to
       * pollute sentry
       */
      /* eslint-disable camelcase */
      const { envelope_fingerprint, signature_fingerprint } = state.requestedEnvelope
      const captureSentryMessage = (message) => {
        Sentry.captureMessage(message)
        Sentry.configureScope((scope) => {
          scope.setExtra('URL', `https://sign.signable.app/#/${envelope_fingerprint}/${signature_fingerprint}`)
        })
      }
      if (!data.documents.length) {
        captureSentryMessage(`NaN Issue: Documents not loaded for envelope: ${envelope_fingerprint}; signature: ${signature_fingerprint}. Given type: ${typeof data.documents}`)
      }
      if (data.documents) { // don't error if the JSON payload is incomplete
        /**
         * the following 2 conditions might be thrown in SideBarInfo
         * component https://gitlab.wearedomo.com/domo/signable/signing-page/blob/25ef91a0e3ba462ace1478a0f8994fbf3bd3b437/src/components/SideBarInfo.vue#L21
         */
        if (!state.widget.fields && !data.documents[0].num_pages) {
          captureSentryMessage(`NaN Issue: Property "num_pages" not provided. Envelope: ${envelope_fingerprint}; signature: ${signature_fingerprint}. Given type: ${typeof data.documents[0].num_pages}`)
        }
        if (!!state.widget.fields && !data.documents.pages) {
          captureSentryMessage(`NaN Issue: Property "pages" not provided. Envelope: ${envelope_fingerprint}; signature: ${signature_fingerprint}. Given type: ${typeof data.documents.pages}`)
        }
      }
      /* eslint-enable camelcase */

      state.clientInfo = data.client // Set client info

      data.documents.forEach((doc) => { // Set documents
        state.envelope.documents.push(doc)
      })

      state.envelope.documents.forEach((document) => {
        Object.values(document.fields).forEach((page) => {
          page.forEach((field) => {
            if (field.merge_field && field.content) field.prefilled = true
          })
        })
      })
    },
    setActiveField: (state, { activeFieldId }) => {
      state.activeFieldId = activeFieldId
    },
    setCurrentFieldValue: (state, { field, val, property, isWidget }) => {
      const updatedFields = isWidget ? state.widget.fields : state.envelope.documents[field.docIndex].fields

      updatedFields[field.page][field.index][property] = val
      setWith(updatedFields, `${field.page}.${field.index}.${property}`, val, function (nsValue, key, nsObject) {
        return Vue.set(nsObject, key, nsValue)
      })
    },
    removePrevFieldSignatures: (state, { field, property }) => {
      switch (property) {
        case 'drawn_json':
          field.content = null
          field.upload = null
          break
        case 'content':
          field.drawn_json = null
          field.upload = null
          break
        default:
          field.drawn_json = null
          field.content = null
      }
    },
    setActiveTab: (state, { activeTabId, tabProperty }) => {
      state.activeTabId = activeTabId
      state.signatureToUse = tabProperty
    },
    setLatestSignature: (state, field) => {
      const {
        content,
        drawn_json, // eslint-disable-line camelcase
        upload
      } = field

      state.latestSignature = {
        content,
        drawn_json,
        upload
      }
    },
    setMobile: (state, { isMobile }) => {
      state.isMobile = isMobile
    },
    setUploader: (state, { uploader, fieldId }) => {
      state.uploaders[fieldId] = uploader
    },
    incrementSequence: (state, { newSequenceNumber }) => {
      state.sequenceGuideNumber = newSequenceNumber
    },
    nextDocument: (state, { index }) => {
      state.currentDocument = index
    },
    setRequestedEnvelope: (state, requestedEnvelope) => {
      state.requestedEnvelope = requestedEnvelope
    }
  },
  actions: {
    getEnvelope: ({ getters, commit }, { query, password }) => {
      const requestedEnvelope = getters.requestedEnvelope

      if (router.currentRoute.name === 'SignWidget') {
        return axios.get(`${process.env.VUE_APP_API_URL}/data/widget?widget=${requestedEnvelope.widgetCode}`)
          .then((response) => {
            commit('setWidget', { widget: response.data, mergeFields: query })
            amplitude.getInstance().logEvent('envelope_loaded_widget')
            return response
          })
      } else if (router.currentRoute.name === 'Preview') {
        return axios.get(`${process.env.VUE_APP_API_URL}/data/preview?fingerprint=${requestedEnvelope.envelopeFingerprint}`)
          .then((response) => {
            commit('setEnvelopeRegular', { data: response.data })

            return response
          })
      }

      return axios.get(`${process.env.VUE_APP_API_URL}/data/envelope`, {
        params: {
          'passphrase': password
        }
      }).then((response) => {
        if (response.data.error) {
          throw response
        }
        commit('setEnvelopeRegular', { data: response.data })
        amplitude.getInstance().setUserId(response.data.client.client_id)
        amplitude.getInstance().logEvent('envelope_loaded_regular')
        return response
      })
    },
    setCurrentFieldVal: ({ commit, getters }, { val, id, property }) => {
      const field = getters.getField(id, false)
      const isWidget = getters.isWidget
      commit('setCurrentFieldValue', { field, val, property, isWidget })

      if (field.type === 'signature') commit('setLatestSignature', field)
    },
    removePrevFieldSignatures: ({ commit, getters }, { id, property }) => {
      const field = getters.getField(id, false)
      commit('removePrevFieldSignatures', { field, property })
    },
    setUploader: ({ commit }, { uploader, fieldId }) => {
      commit('setUploader', { uploader, fieldId })
    },
    nextDocument: ({ commit, getters, state }) => {
      const incompleteDoc = state.envelope.documents.findIndex((doc) => !getters.isDocumentDone(doc.fingerprint))
      commit('nextDocument', { index: incompleteDoc })
    }
  },
  getters: {
    allFields: (state) => {
      let fields = []

      const extractFields = (docFields, docIndex) => {
        const extractedFields = []
        const documentFields = Object.values(docFields)
        const pages = Object.keys(docFields)
        documentFields.forEach((pageFields, page) => {
          pageFields.forEach((field, index) => { // TODO Set doc number
            field.docIndex = docIndex
            field.page = pages[page]
            field.index = index
            extractedFields.push(field)
          })
        })

        return extractedFields
      }

      if (state.widget.fields) {
        fields = extractFields(state.widget.fields)
      } else if (state.envelope && state.envelope.documents.length > 0) {
        state.envelope.documents.forEach((doc, docIndex) => {
          fields = fields.concat(extractFields(doc.fields, docIndex))
        })
      }

      // when merge fields come back, 2 always comeback one with value,
      // other without. This function sanitizes this bug
      fields = Object.values(fields.reduce((acc, cur) =>
        Object.assign(acc, { [cur.id]: cur }), {}
      ))

      return fields
    },
    fieldsForParty: (state, getters) => {
      const allFields = getters.allFields
      const currentPartyId = getters.currentPartyId

      return allFields.filter(field => field.party_id === currentPartyId)
    },
    fieldIsDone: (state, getters) => (fieldId) => {
      const field = getters.allFields.find(f => f.id === fieldId)
      return field.content || (field.drawn_json && field.drawn_json.length > 0) || field.upload
    },
    requiredFieldCount: (state, getters) => getters.fieldsForParty.filter(field => field.required === 'true' && !(field.is_merge_field && field.prefilled)).length,
    doneRequiredFieldCount: (state, getters) => getters.doneFieldsForAllDocuments.filter(field => field.required === 'true' && !(field.is_merge_field && field.prefilled)).length,
    outstandingFieldsNo: (state, getters) => getters.requiredFieldCount - getters.doneRequiredFieldCount,
    /**
     * @return {Number}
     */
    envelopeCompletionPercent: (state, getters) => {
      if (getters.requiredFieldCount === 0) {
        return 100
      } else if (getters.doneRequiredFieldCount === 0) {
        return 0
      }

      return Math.round((getters.doneRequiredFieldCount / getters.requiredFieldCount) * 100)
    },
    isEnvelopeComplete: (state, getters) => () => getters.envelopeCompletionPercent === 100,
    // Only show questions if company has allowed, and not widget
    canShowQuestions: (state, getters) => getters.config('company_questions_signing') === '1' && !getters.isWidget,
    envelopeDocuments: state => state.envelope.documents,
    latestSignature: state => state.latestSignature,
    currentDocument: (state) => {
      if (state.envelope.documents && state.envelope.documents.length > 0) return state.envelope.documents[state.currentDocument]

      const widget = store.state.widget
      widget.pages = parseInt(widget.pages, 10)
      return widget
    },
    isWidget: state => !!state.widget.fields,
    widgetRedirectUrl: state => state.widget.redirect_url,
    title: state => state.envelope.title,
    requestedEnvelope: state => state.requestedEnvelope,
    config: state => property => state.config[property],
    document: state => index => state.envelope.documents[index],
    /**
     * Get the current document fingerprint
     * @return [String,Boolean] Returns a string or boolean
     */
    currentDocumentFingerPrint: (state, getters) => {
      if (state.widget && state.widget.fingerprint) return state.widget.fingerprint
      const doc = getters.document(state.currentDocument)
      if (doc) return doc.fingerprint

      return false
    },
    /**
     * Returns the current party id if available, else undefined
     * @return [String,undefined]
     */
    currentPartyId: (state) => {
      let currentPartyId
      state.parties.forEach((party) => {
        if (party.isSigner) {
          currentPartyId = party.party_id
        }
      })
      return currentPartyId
    },
    /**
     * Get the current party from the store
     * @return {Array<Object>}
     */
    currentParty: (state, getters) => {
      const currentPartyId = getters.currentPartyId
      return state.parties.filter(party => currentPartyId === party.party_id)[0]
    },
    getField: (state, getters) => (id, returnValue = true, property) => {
      let allFields = getters.allFields

      const matchedField = allFields.filter(field => parseInt(field.id, 10) === parseInt(id, 10))

      if (matchedField.length > 0) {
        if (returnValue) return matchedField[0][property]

        return matchedField[0]
      }

      return ''
    },
    requiredFieldsForParty: (state, getters) => {
      const fieldsForParty = getters.fieldsForParty

      return fieldsForParty.filter((field) => {
        const isRequired = field.required === 'true'
        return isRequired && !(field.is_merge_field && field.prefilled)
      })
    },
    fieldsForDocument: (state, getters) => (documentFingerprint = null) => {
      const documentIndex = state.envelope.documents.findIndex(document => document.fingerprint === documentFingerprint)
      const currentDoc = documentFingerprint === null ? state.currentDocument : documentIndex
      const doc = state.widget.fields ? state.widget : state.envelope.documents[currentDoc]
      const fields = []

      if (doc) {
        const documentFields = Object.values(doc.fields)
        const currentPartyId = getters.currentPartyId
        documentFields.forEach((pageFields) => {
          pageFields.forEach((field) => {
            const isForCurrentParty = field.party_id === currentPartyId
            if (isForCurrentParty && !field.prefilled) fields.push(field)
          })
        })
      }

      return fields
    },
    activeFieldId: (state) => {
      return state.activeFieldId
    },
    nextField: (state, getters) => () => {
      const fields = getters.fieldsForDocument()
      const doneFields = getters.doneFieldsForDocument()
      const activeFieldId = state.activeFieldId

      let nextField = null // TODO Investigate why we're returning either an empty array or an integer

      if (activeFieldId) {
        const activeFieldIndex = fields.findIndex(field => field.id === activeFieldId)
        const currentField = fields[activeFieldIndex]
        if (currentField && currentField.required === 'true' && !getters.fieldDone(currentField)) {
          nextField = currentField
        } else if (Number.isInteger(activeFieldIndex) && activeFieldIndex < fields.length - 1) {
          nextField = fields[activeFieldIndex + 1]
        }
      }

      if (fields && doneFields && !nextField) nextField = fields.find(field => !doneFields.filter(doneField => field.id === doneField.id).length) // Find next field that isn't in the doneFields array

      return nextField
    },
    fieldDone: (state, getters) => (field) => field.content || (field.drawn_json && field.drawn_json.length > 0) || field.upload,
    doneFieldsForDocument: (state, getters) => (documentFingerprint) => {
      const fieldsForCurrentDoc = documentFingerprint ? getters.fieldsForDocument(documentFingerprint) : getters.fieldsForDocument()
      const doneFields = fieldsForCurrentDoc.filter(field => field.content || (field.drawn_json && field.drawn_json.length > 0) || field.upload)
      return doneFields
    },
    doneFieldsForAllDocuments: (state, getters) => {
      return getters.fieldsForParty.filter(field => field.content || (field.drawn_json && field.drawn_json.length > 0) || field.upload)
    },
    isDocumentDone: (state, getters) => (documentFingerprint) => {
      const fingerprint = documentFingerprint || getters.currentDocumentFingerPrint
      const requiredFieldsForCurrentDoc = getters.fieldsForDocument(fingerprint).filter(field => field.required === 'true' && !(field.is_merge_field && field.prefilled))
      const doneRequiredFieldsForDocument = getters.doneFieldsForDocument(fingerprint).filter(field => field.required === 'true' && !(field.is_merge_field && field.prefilled))

      if (doneRequiredFieldsForDocument.length >= requiredFieldsForCurrentDoc.length) return true
      return false
    },
    currentSignature: state => state.signatures[state.signatureToUse],
    isCurrentSignatureImage: (state, getters) => {
      if (!getters.currentSignature) return false

      return getters.currentSignature.indexOf('data:image') !== -1
    },
    footerNextButtonText: (state, getters) => () => { // TODO Move to component
      const isEnvelopeComplete = getters.isEnvelopeComplete()
      const isDocumentDone = getters.isDocumentDone()
      const nextField = getters.nextField()

      if (isEnvelopeComplete && getters.activeFieldId) {
        return i18n.t('functional.confirm')
      } else if (isEnvelopeComplete) {
        return state.config.company_consumer_regulations === '1' ? i18n.t('footer.order') : i18n.t('footer.submit')
      } else if (isDocumentDone) {
        return i18n.t('footer.nextDocument')
      } else if (nextField) {
        const hasTitle = nextField.title !== 'Untitled'
        const buttonText = hasTitle ? nextField.title : i18n.t(`fieldPopup.type.${nextField.type}`)
        return i18n.t('footer.nextField', { field: buttonText })
      }

      return ''
    }
  }
})

export default store
